import type { APIFilterableCars } from '@/scopes/checkout/filterableCars/infrastructure/api/types';
import type { FilterableCarItem, FilterableCars } from '@/scopes/checkout/filterableCars/domain/types/filterableCars';
import {
  isValidUrl,
  mapExtraFeatures,
  mapFeatures,
} from '@/scopes/checkout/infrastructure/api/model-mappers/serviceCarRentalOptionMapper';
import type { FilterMappers } from '@/scopes/checkout/filterableCars/domain/types/filterMappers';
import type { CarFilters } from '@/scopes/checkout/filterableCars/domain/types';

export interface GetAllCarsResponseMapper {
  map: (data: APIFilterableCars) => FilterableCars;
}

interface Dependencies {
  filtersMappers: FilterMappers;
}

export const createGetAllCarsResponseMapper = (dependencies: Dependencies): GetAllCarsResponseMapper => {
  const { filtersMappers: mappers } = dependencies;

  function map(data: APIFilterableCars): FilterableCars {
    const { cars, filters } = data;

    return {
      filters: {
        ...Object.entries(mappers).reduce((acc, [filterType, filterMapper]) => {
          if (!filters) return acc;
          const filterData = filters.find((filter) => filter.type === filterType);
          if (!filterData) return acc;
          const mappedFilter = filterMapper.map(filterData);
          if (!mappedFilter) return acc;

          return {
            ...acc,
            [filterType]: mappedFilter,
          };
        }, {}),
      } as CarFilters,
      cars: cars.reduce<Record<string, FilterableCarItem>>((acc2, car) => {
        const imageUrlLarge = isValidUrl(car.imagePath?.large ?? '') ? car.imagePath?.large : '';
        const imageUrlSmall = isValidUrl(car.imagePath?.small ?? '') ? car.imagePath?.small : '';
        const id = car.uniqueId ?? car.code;
        return {
          ...acc2,
          [id]: {
            id,
            value: car.code,
            price: car.price,
            image: imageUrlLarge ?? imageUrlSmall ?? '',
            carClass: car.carClass ?? '',
            label: car.carModel ?? '',
            conditionsUrl: car.conditionsUrl ?? '',
            availableOnRequest: car.availableOnRequest ?? false,
            features: mapFeatures(car) ?? [],
            extraFeatures: mapExtraFeatures(car) ?? [],
          },
        };
      }, {}),
    };
  }
  return { map };
};
